
import { PropertyFieldConfig } from '../components/molecules/PropertyField';

export interface TabConfig {
    id: string;
    label: string;
    fields: PropertyFieldConfig[];
    specialComponent?: string; // Identifier for special React components (e.g., 'RoutingTab')
    layout?: 'default' | 'fullscreen'; // UI hint for maximum space
}

export interface NodePropertyConfig {
    type: string;
    tabs: TabConfig[];
}

// Helper to create common fields
const commonFields: PropertyFieldConfig[] = [
    { name: 'label', label: 'Display Label', type: 'text', validation: { required: true } },
    { name: 'description', label: 'Description', type: 'textarea', rows: 2 },
];

const boundaryFields: PropertyFieldConfig[] = [
    ...commonFields,
    { 
        name: 'cancelActivity', 
        label: 'Interrupting', 
        type: 'boolean', 
        description: 'Cancel the attached activity when triggered',
        defaultValue: true 
    }
];

export const PROPERTY_REGISTRY: Record<string, NodePropertyConfig> = {
    // --- Start Events ---
    'BlankStartEvent': {
        type: 'BlankStartEvent',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields }
        ]
    },
    'FormStartEvent': {
        type: 'FormStartEvent',
        tabs: [
            { id: 'properties', label: 'General', fields: [...commonFields, { name: 'formTitle', label: 'Form Title', type: 'text' }] },
            { id: 'form', label: 'Form Builder', fields: [], specialComponent: 'FormBuilderTab', layout: 'fullscreen' }
        ]
    },
    'TimerStartEvent': {
        type: 'TimerStartEvent',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'timer', label: 'Timer', fields: [], specialComponent: 'TimerTab' }
        ]
    },
    'MessageStartEvent': {
        type: 'MessageStartEvent',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'message', label: 'Message', fields: [], specialComponent: 'MessageTab' }
        ]
    },
    'SignalStartEvent': {
        type: 'SignalStartEvent',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'signal', label: 'Signal', fields: [], specialComponent: 'SignalTab' }
        ]
    },

    // --- Tasks ---
    'UserTask': {
        type: 'UserTask',
        tabs: [
            { id: 'properties', label: 'General', fields: [...commonFields, { name: 'userTaskType', label: 'Task Type', type: 'select', options: [{value:'generic', label:'Generic'}, {value:'produce', label:'Badge Production'}, {value:'smartbridge', label:'SmartBridge'}, {value:'receipt', label:'Receipt View'}] }] },
            { id: 'form', label: 'Form Builder', fields: [], specialComponent: 'FormBuilderTab', layout: 'fullscreen' },
            { id: 'assignments', label: 'Assignments', fields: [], specialComponent: 'AssignmentsTab' },
            { id: 'extensions', label: 'Extensions', fields: [], specialComponent: 'UserTaskExtensionTab', layout: 'fullscreen' }
        ]
    },
    'ScriptTask': {
        type: 'ScriptTask',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'script', label: 'Script', fields: [], specialComponent: 'ScriptTab', layout: 'fullscreen' }
        ]
    },
    'ServiceTaskREST': {
        type: 'ServiceTaskREST',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'http', label: 'HTTP Config', fields: [], specialComponent: 'RestConfigTab', layout: 'fullscreen' },
            { id: 'extraction', label: 'Extraction', fields: [], specialComponent: 'ExtractionTab', layout: 'fullscreen' }
        ]
    },
    'ServiceTaskLLM': {
        type: 'ServiceTaskLLM',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'llm', label: 'LLM Config', fields: [], specialComponent: 'LLMConfigTab', layout: 'fullscreen' },
            { id: 'extraction', label: 'Extraction', fields: [], specialComponent: 'ExtractionTab', layout: 'fullscreen' }
        ]
    },
    'ServiceTaskRules': {
        type: 'ServiceTaskRules',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'decision', label: 'Decision Config', fields: [], specialComponent: 'DecisionConfigTab', layout: 'fullscreen' },
            { id: 'extraction', label: 'Extraction', fields: [], specialComponent: 'ExtractionTab', layout: 'fullscreen' }
        ]
    },
    'ServiceTaskEmail': {
        type: 'ServiceTaskEmail',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'email', label: 'Email Template', fields: [], specialComponent: 'EmailTemplateTab', layout: 'fullscreen' }
        ]
    },
    'ServiceTaskSMS': {
        type: 'ServiceTaskSMS',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'sms', label: 'SMS Config', fields: [], specialComponent: 'SmsTab', layout: 'fullscreen' }
        ]
    },
    'ServiceTaskDB': {
        type: 'ServiceTaskDB',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'database', label: 'Database', fields: [], specialComponent: 'DatabaseTab', layout: 'fullscreen' },
            { id: 'extraction', label: 'Extraction', fields: [], specialComponent: 'ExtractionTab', layout: 'fullscreen' }
        ]
    },
    'ServiceTaskMCP': {
        type: 'ServiceTaskMCP',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'mcp', label: 'MCP Config', fields: [], specialComponent: 'McpConfigTab', layout: 'fullscreen' },
            { id: 'extraction', label: 'Extraction', fields: [], specialComponent: 'ExtractionTab', layout: 'fullscreen' }
        ]
    },

    // --- Gateways ---
    'XorGateway': {
        type: 'XorGateway',
        tabs: [
            { id: 'properties', label: 'General', fields: [...commonFields, { name: 'isMerging', label: 'Is Merging', type: 'boolean' }] },
            { id: 'routing', label: 'Routing', fields: [], specialComponent: 'ConditionsTab', layout: 'fullscreen' }
        ]
    },
    'InclusiveGateway': {
        type: 'InclusiveGateway',
        tabs: [
            { id: 'properties', label: 'General', fields: [...commonFields, { name: 'isMerging', label: 'Is Merging', type: 'boolean' }] },
            { id: 'routing', label: 'Routing', fields: [], specialComponent: 'ConditionsTab', layout: 'fullscreen' }
        ]
    },
    'ParallelGateway': {
        type: 'ParallelGateway',
        tabs: [
            { id: 'properties', label: 'General', fields: [...commonFields, { name: 'isMerging', label: 'Is Merging', type: 'boolean' }] },
            { id: 'routing', label: 'Config', fields: [], specialComponent: 'ConditionsTab', layout: 'fullscreen' }
        ]
    },
    'EventBasedGateway': {
        type: 'EventBasedGateway',
        tabs: [{ id: 'properties', label: 'General', fields: commonFields }]
    },

    // --- Activities ---
    'CallActivity': {
        type: 'CallActivity',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'call', label: 'Process Call', fields: [], specialComponent: 'CallActivityTab', layout: 'fullscreen' }
        ]
    },
    'SubProcess': {
        type: 'SubProcess',
        tabs: [{ id: 'properties', label: 'General', fields: commonFields }]
    },

    // --- End Events ---
    'BlankEndEvent': { type: 'BlankEndEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }] },
    'ErrorEndEvent': { 
        type: 'ErrorEndEvent', 
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { 
                id: 'error', 
                label: 'Error Config', 
                fields: [
                    { 
                        name: 'errorMessage', 
                        label: 'Error Message', 
                        type: 'text', 
                        placeholder: 'e.g. Payment Failed',
                        description: 'A human-readable message for the error.'
                    },
                    { 
                        name: 'errorPayload', 
                        label: 'Error Payload (Script)', 
                        type: 'expression', 
                        rows: 6,
                        placeholder: '{\n  code: 500,\n  reason: variables.reason\n}',
                        description: 'Expression returning error data.' 
                    }
                ] 
            }
        ] 
    },
    'TerminateEndEvent': { type: 'TerminateEndEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }] },
    'MessageEndEvent': { type: 'MessageEndEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }, { id: 'message', label: 'Message', fields: [], specialComponent: 'MessageThrowTab' }] },
    'SignalEndEvent': { type: 'SignalEndEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }, { id: 'signal', label: 'Signal', fields: [], specialComponent: 'SignalThrowTab' }] },

    // --- Intermediate Events ---
    'IntermediateCatchTimerEvent': { type: 'IntermediateCatchTimerEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }, { id: 'timer', label: 'Timer', fields: [], specialComponent: 'TimerTab' }] },
    'IntermediateCatchMessageEvent': { type: 'IntermediateCatchMessageEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }, { id: 'message', label: 'Message', fields: [], specialComponent: 'MessageTab' }] },
    'IntermediateCatchSignalEvent': { type: 'IntermediateCatchSignalEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }, { id: 'signal', label: 'Signal', fields: [], specialComponent: 'SignalTab' }] },
    'IntermediateThrowMessageEvent': { type: 'IntermediateThrowMessageEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }, { id: 'message', label: 'Message', fields: [], specialComponent: 'MessageThrowTab' }] },
    'IntermediateThrowSignalEvent': { type: 'IntermediateThrowSignalEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }, { id: 'signal', label: 'Signal', fields: [], specialComponent: 'SignalThrowTab' }] },

    // --- Boundary Events ---
    'TimerBoundaryEvent': { type: 'TimerBoundaryEvent', tabs: [{ id: 'properties', label: 'General', fields: boundaryFields }, { id: 'timer', label: 'Timer', fields: [], specialComponent: 'TimerTab' }] },
    'MessageBoundaryEvent': { type: 'MessageBoundaryEvent', tabs: [{ id: 'properties', label: 'General', fields: boundaryFields }, { id: 'message', label: 'Message', fields: [], specialComponent: 'MessageTab' }] },
    'SignalBoundaryEvent': { type: 'SignalBoundaryEvent', tabs: [{ id: 'properties', label: 'General', fields: boundaryFields }, { id: 'signal', label: 'Signal', fields: [], specialComponent: 'SignalTab' }] },
    'ErrorBoundaryEvent': { 
        type: 'ErrorBoundaryEvent', 
        tabs: [
            { 
                id: 'properties', 
                label: 'General', 
                fields: [
                    ...boundaryFields,
                    { 
                        name: 'exceptionType', 
                        label: 'Error Name (Catch-all if empty)', 
                        type: 'text', 
                        placeholder: 'e.g. ValidationError',
                        description: 'Leave empty to catch all errors.' 
                    }
                ] 
            }
        ] 
    }
};
